import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '@services/apiClient';
import { fetchReading } from '@services/api';
import { WorkspaceReadingView, ReadingWithUserStatus } from '@/types/models';
import type { RootState } from '@store/index';
import {
  addReading,
  renameReading as renameReadingReducer,
  removeReading as removeReadingReducer,
} from '@store/slices/workspaceSlice';

interface WorkspaceReadingsState {
  allWorkspaceReadings: WorkspaceReadingView[];
  loading: boolean;
  error: string | null;
}

const initialState: WorkspaceReadingsState = {
  allWorkspaceReadings: [],
  loading: false,
  error: null,
};

// Async Thunks

export const loadAllWorkspaceReadings = createAsyncThunk(
  'workspaceReadings/loadAll',
  async (workspaceId: string, { rejectWithValue }) => {
    try {
      // The /manage endpoint returns WorkspaceReadingView (with
      // assigned_by_username + workflow aggregates). The bare /readings
      // endpoint returns per-user Reading rows that lack those fields,
      // so the manage tab would display "Unknown" for assignment after
      // any reload.
      const response = await apiClient.get<{
        readings: WorkspaceReadingView[] | null;
      }>(`/workspaces/${workspaceId}/readings/manage`);
      return response.data.readings ?? [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const createReading = createAsyncThunk(
  'workspaceReadings/create',
  async (payload: {
    workspace_id: string;
    document_id: string;
    reading_name: string;
    reading_data?: string;
    // Assignment metadata is optional at creation — it belongs to the
    // separate (gated) assign flow. Creation just needs the document + name.
    description?: string;
    instructions?: string;
    due_date?: string;
    required?: boolean;
    assign_to_all?: boolean;
    assign_to_users?: string[];
  }, { dispatch, rejectWithValue }) => {
    try {
      // Default reading_data if not provided
      const requestPayload = {
        ...payload,
        reading_data: payload.reading_data ?? JSON.stringify({ highlights: [] }),
      };
      const response = await apiClient.post<WorkspaceReadingView>(
        `/workspaces/${payload.workspace_id}/readings`,
        requestPayload
      );
      // The create response (WorkspaceReadingView) carries no presigned
      // document_url, so the reader can't load the PDF until a refresh
      // (selectCurrentPdfUrl reads reading.document_url). Fetch the full
      // reading — GET /readings/:id presigns the document — and sync that
      // complete object so the PDF loads immediately, without a reload.
      const full = await fetchReading(response.data.reading_id);
      dispatch(
        addReading(
          full.success
            ? full.data
            : (response.data as unknown as ReadingWithUserStatus)
        )
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updateReading = createAsyncThunk(
  'workspaceReadings/update',
  async ({ readingId, data }: {
    readingId: string;
    data: {
      reading_name?: string;
      description?: string;
      instructions?: string;
      due_date?: string;
      required?: boolean;
    };
  }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/readings/${readingId}`, data);
      // Mirror rename into the per-user slice when the name changed.
      if (data.reading_name) {
        dispatch(
          renameReadingReducer({ readingId, readingName: data.reading_name })
        );
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deleteReading = createAsyncThunk<
  string,
  string,
  { state: RootState; rejectValue: string }
>(
  'workspaceReadings/delete',
  async (readingId, { getState, dispatch, rejectWithValue }) => {
    const workspaceId = getState().workspace.workspace_id;
    if (!workspaceId) {
      return rejectWithValue('No active workspace');
    }
    try {
      await apiClient.delete(
        `/workspaces/${workspaceId}/readings/${readingId}`
      );
      // Mirror removal into the per-user slice so the reader cascade fires.
      dispatch(removeReadingReducer(readingId));
      return readingId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const assignReading = createAsyncThunk(
  'workspaceReadings/assign',
  async ({ readingId, userIds }: { readingId: string; userIds: string[] }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/readings/${readingId}/permissions`, {
        user_ids: userIds,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// Slice

const workspaceReadingsSlice = createSlice({
  name: 'workspaceReadings',
  initialState,
  reducers: {
    clearWorkspaceReadings: (state) => {
      state.allWorkspaceReadings = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // loadAllWorkspaceReadings
      .addCase(loadAllWorkspaceReadings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadAllWorkspaceReadings.fulfilled, (state, action) => {
        state.loading = false;
        state.allWorkspaceReadings = action.payload;
      })
      .addCase(loadAllWorkspaceReadings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to load workspace readings';
      })
      // createReading
      .addCase(createReading.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReading.fulfilled, (state, action) => {
        state.loading = false;
        state.allWorkspaceReadings.unshift(action.payload);
      })
      .addCase(createReading.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to create reading';
      })
      // updateReading
      .addCase(updateReading.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReading.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.allWorkspaceReadings.findIndex(
          r => r.reading_id === action.payload.reading_id
        );
        if (index !== -1) {
          // Merge the updated fields
          state.allWorkspaceReadings[index] = {
            ...state.allWorkspaceReadings[index],
            ...action.payload,
          };
        }
      })
      .addCase(updateReading.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to update reading';
      })
      // deleteReading
      .addCase(deleteReading.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteReading.fulfilled, (state, action) => {
        state.loading = false;
        state.allWorkspaceReadings = state.allWorkspaceReadings.filter(
          r => r.reading_id !== action.payload
        );
      })
      .addCase(deleteReading.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to delete reading';
      });
  },
});

// Actions
export const { clearWorkspaceReadings } = workspaceReadingsSlice.actions;

// Selectors
export const selectAllWorkspaceReadings = (state: RootState) =>
  state.workspaceReadings.allWorkspaceReadings;
export const selectReadingsLoading = (state: RootState) =>
  state.workspaceReadings.loading;
export const selectReadingsError = (state: RootState) =>
  state.workspaceReadings.error;

export default workspaceReadingsSlice.reducer;
